export type StudioOperationImpactSurface = 'app' | 'design' | 'guide' | 'insights' | 'release'

export type StudioOperationImpactAuthority =
  | 'studio-state'
  | 'external-receipt'
  | 'publication-control-plane'

export interface StudioOperationImpactContract {
  surface: StudioOperationImpactSurface
  affectedSurfaces?: readonly StudioOperationImpactSurface[]
  authority: StudioOperationImpactAuthority
  paths: (params: Record<string, unknown>) => string[]
}

function value(params: Record<string, unknown>, key: string, fallback: string): string {
  const candidate = params[key]
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : fallback
}

function segment(candidate: string): string {
  return encodeURIComponent(candidate)
}

function fields(base: string, params: Record<string, unknown>, keys: readonly string[]): string[] {
  const present = keys.filter((key) => Object.prototype.hasOwnProperty.call(params, key))
  return (present.length > 0 ? present : keys).map((key) => `${base}/${segment(key)}`)
}

function applicationPatchPaths(params: Record<string, unknown>): string[] {
  if (!Array.isArray(params.patches)) return ['/application']
  const paths = params.patches
    .map((patch) => {
      if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return null
      const path = (patch as Record<string, unknown>).path
      return typeof path === 'string' && path.startsWith('/') ? path : null
    })
    .filter((path): path is string => Boolean(path))
  return paths.length > 0 ? paths : ['/application']
}

function pageMetaPaths(params: Record<string, unknown>): string[] {
  const pageId = segment(value(params, 'pageId', 'selected'))
  return fields(`/manifest/pages/${pageId}`, params, ['title', 'description', 'route', 'layout'])
}

function elementUpdatePaths(params: Record<string, unknown>): string[] {
  const blockName = segment(value(params, 'blockName', 'selected'))
  const elementId = segment(value(params, 'elementId', 'selected'))
  const changes = params.changes
  const keys =
    changes && typeof changes === 'object' && !Array.isArray(changes)
      ? Object.keys(changes as Record<string, unknown>)
      : []
  return keys.length > 0
    ? keys.map((key) => `/blocks/${blockName}/ui/${elementId}/${segment(key)}`)
    : [`/blocks/${blockName}/ui/${elementId}`]
}

function documentNodePaths(params: Record<string, unknown>): string[] {
  const nodeId = segment(value(params, 'nodeId', 'selected'))
  const keys = ['text', 'title', 'attributes'].filter((key) =>
    Object.prototype.hasOwnProperty.call(params, key),
  )
  return (keys.length > 0 ? keys : ['node']).map((key) =>
    key === 'node'
      ? `/cogoDocumentWorkspace/nodes/${nodeId}`
      : `/cogoDocumentWorkspace/nodes/${nodeId}/${segment(key)}`,
  )
}

const state = (
  surface: StudioOperationImpactSurface,
  paths: StudioOperationImpactContract['paths'],
): StudioOperationImpactContract => ({
  surface,
  authority: 'studio-state',
  paths,
})

const external = (
  surface: StudioOperationImpactSurface,
  paths: StudioOperationImpactContract['paths'],
): StudioOperationImpactContract => ({
  surface,
  authority: 'external-receipt',
  paths,
})

const publication = (
  paths: StudioOperationImpactContract['paths'],
): StudioOperationImpactContract => ({
  surface: 'release',
  authority: 'publication-control-plane',
  paths,
})

export const STUDIO_OPERATION_EXACT_IMPACT_CONTRACTS = {
  'workspace.semantic-sync': {
    surface: 'app',
    affectedSurfaces: ['app', 'design', 'guide', 'insights', 'release'],
    authority: 'publication-control-plane',
    paths: (params) => [
      `/manifest/pages/${segment(value(params, 'pageId', 'selected'))}/title`,
      '/tokens/colors/primary',
      `/cogoDocumentWorkspace/nodes/${segment(value(params, 'documentNodeId', 'selected'))}/text`,
      '/publication/releases/candidates',
    ],
  },
  'page.details.rename': state('app', pageMetaPaths),
  'page.details.route': state('app', pageMetaPaths),
  'page.details.blocks': state('app', (params) => [
    `/manifest/pages/${segment(value(params, 'pageId', 'selected'))}/blocks`,
  ]),
  'page.details.layout': state('app', pageMetaPaths),
  'page.details.delete': state('app', (params) => [
    `/manifest/pages/${segment(value(params, 'pageId', 'selected'))}`,
  ]),
  'element.add': state('app', (params) => [
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/ui`,
  ]),
  'element.update': state('app', elementUpdatePaths),
  'element.remove': state('app', (params) => [
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/ui/${segment(value(params, 'elementId', 'selected'))}`,
  ]),
  'flow.create': state('app', (params) => [
    `/flows/${segment(value(params, 'name', 'selected'))}`,
  ]),
  'flow.rename': state('app', (params) => [
    `/flows/${segment(value(params, 'flowId', value(params, 'name', 'selected')))}/name`,
  ]),
  'behavior.flow.add-step': state('app', (params) => [
    `/flows/${segment(value(params, 'flowId', 'selected'))}/steps`,
  ]),
  'behavior.flow.connect-selected': state('app', (params) => [
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/events/${segment(value(params, 'eventKey', 'selected'))}`,
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/flow/${segment(value(params, 'flow', 'selected'))}`,
  ]),
  'event-binding.remove': state('app', (params) => [
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/events/${segment(value(params, 'eventKey', 'selected'))}`,
  ]),
  'behavior.flow.delete': state('app', (params) => [
    `/flows/${segment(value(params, 'flowId', 'selected'))}`,
  ]),
  'variable.add': state('app', (params) => [
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/variables/${segment(value(params, 'scope', 'state'))}/${segment(value(params, 'name', 'selected'))}`,
  ]),
  'variable.update': state('app', (params) => [
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/variables/${segment(value(params, 'scope', 'state'))}/${segment(value(params, 'name', 'selected'))}`,
  ]),
  'variable.remove': state('app', (params) => [
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/variables/${segment(value(params, 'scope', 'state'))}/${segment(value(params, 'name', 'selected'))}`,
  ]),
  'data.source.add': state('app', (params) => [
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/data/${segment(value(params, 'name', 'selected'))}`,
  ]),
  'data.source.scaffold-from-chat': state('app', (params) => [
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/data/${segment(value(params, 'name', 'selected'))}`,
  ]),
  'datasource.update': state('app', (params) => [
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/data/${segment(value(params, 'name', 'selected'))}`,
  ]),
  'data.binding.connect': state('app', (params) => [
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/ui/${segment(value(params, 'elementId', 'selected'))}/dataBinding`,
  ]),
  'data.source.remove': state('app', (params) => [
    `/blocks/${segment(value(params, 'blockName', 'selected'))}/data/${segment(value(params, 'name', 'selected'))}`,
  ]),
  'application.generate': external('app', () => ['/applications/generationRequests']),
  'application.create-from-brief': external('app', () => ['/applications/candidates']),
  'application.patch': state('app', applicationPatchPaths),
  'application.bundle.export': external('app', (params) => [
    `/exports/cogo-bundle/${segment(value(params, 'bundleId', 'current'))}`,
  ]),
  'figma.source.import': state('design', () => ['/sourceWorkspace/import']),
  'figma.token.validate': external('design', () => ['/sourceWorkspace/tokenValidation']),
  'figma.frame.import': external('design', (params) => [
    `/sourceWorkspace/figmaFrames/${segment(value(params, 'figmaNodeId', 'selected'))}`,
  ]),
  'github.preview.import': external('app', (params) => [
    `/sourceWorkspace/githubPreviews/${segment(value(params, 'repo', 'selected'))}/${segment(String(params.prNumber ?? 'selected'))}`,
  ]),
  'source.figma.edit': state('design', () => [
    '/sourceWorkspace/bundle',
    '/sourceWorkspace/bundle/revisionLineage',
  ]),
  'source.figma.apply_design_mutation': state('design', () => ['/sourceWorkspace/designMutation']),
  'source.figma.apply_mapping': state('design', () => [
    '/sourceWorkspace/pageDecisions',
    '/sourceWorkspace/sectionDecisions',
    '/sourceWorkspace/surfaceDecisions',
  ]),
  'source.figma.decide_tokens': state('design', () => ['/sourceWorkspace/tokenDecisions']),
  'source.figma.visual_review': external('insights', () => ['/sourceWorkspace/visualJudgements']),
  'source.figma.assemble': state('design', () => ['/sourceWorkspace/assemblyManifest']),
  'source.figma.runtime_proof': external('insights', () => ['/sourceWorkspace/runtimeProof']),
  'source.figma.create_candidate': state('app', () => ['/applications/candidates']),
  'evidence.sufficiency.answer': external('insights', () => [
    '/evidence/source-sufficiency/assessment',
  ]),
  'report.document-page.edit': state('guide', (params) => [
    `/cogoDocumentWorkspace/reportEdits/${segment(value(params, 'target', 'selected'))}`,
  ]),
  'report.document-page.export': external('guide', (params) => [
    `/exports/document/${segment(value(params, 'pageId', 'selected'))}/${segment(value(params, 'target', 'selected'))}`,
  ]),
  'document.page.create': state('guide', () => ['/cogoDocumentWorkspace/pages']),
  'document.page.update_node': state('guide', documentNodePaths),
  'document.page.move': state('guide', () => ['/cogoDocumentWorkspace/pageOrder']),
  'document.page.delete': state('guide', (params) => [
    `/cogoDocumentWorkspace/pages/${segment(value(params, 'pageId', 'selected'))}`,
  ]),
  'document.page.export': external('guide', (params) => [
    `/exports/document/${segment(value(params, 'pageId', 'selected'))}/${segment(value(params, 'target', 'selected'))}`,
  ]),
  'document.page.propose_edit': state('guide', (params) => [
    `/cogoDocumentWorkspace/proposals/${segment(value(params, 'documentNodeId', 'selected'))}`,
  ]),
  'document.proposal.apply': state('guide', (params) => [
    `/cogoDocumentWorkspace/proposals/${segment(value(params, 'proposalId', 'selected'))}/status`,
  ]),
  'document.proposal.reject': state('guide', (params) => [
    `/cogoDocumentWorkspace/proposals/${segment(value(params, 'proposalId', 'selected'))}/status`,
  ]),
  'document.target.export': external('guide', (params) => [
    `/exports/document/${segment(value(params, 'target', 'selected'))}`,
  ]),
  'theme.preset.set': state('design', () => ['/tokens', '/activePresetId']),
  'theme.color.update': state('design', (params) => [
    `/tokens/colors/${segment(value(params, 'key', 'selected'))}`,
  ]),
  'theme.font.set': state('design', () => ['/tokens/fontFamily']),
  'theme.radius.set': state('design', () => ['/tokens/radius']),
  'theme.reset': state('design', () => ['/tokens', '/activePresetId']),
  'project.create': external('app', () => ['/projects']),
  'project.rename': state('app', () => ['/name']),
  'project.metadata.update': state('app', (params) => fields('', params, ['description', 'icon'])),
  'project.delete': state('app', (params) => [
    `/projects/${segment(value(params, 'projectId', 'current'))}`,
  ]),
  'project.duplicate': external('app', () => ['/projects']),
  'release.preview.share': publication(() => ['/releases/previews']),
  'release.verify': publication((params) => [
    `/releases/${segment(value(params, 'releaseId', 'current'))}/verification`,
  ]),
  'release.deploy': publication((params) => [
    `/releases/${segment(value(params, 'releaseId', 'current'))}/deployments/${segment(value(params, 'environment', 'production'))}`,
  ]),
  'release.readback': publication((params) => [
    `/releases/${segment(value(params, 'releaseId', 'current'))}/readback`,
  ]),
  'release.rollback': publication((params) => [
    `/releases/${segment(value(params, 'releaseId', 'current'))}/rollback`,
  ]),
  'project.publish.confirm': publication(() => ['/publication/releases/candidates']),
} as const satisfies Record<string, StudioOperationImpactContract>

export type StudioOperationExactImpactId = keyof typeof STUDIO_OPERATION_EXACT_IMPACT_CONTRACTS

export function getStudioOperationImpactContract(
  operationId: string,
): StudioOperationImpactContract | undefined {
  return STUDIO_OPERATION_EXACT_IMPACT_CONTRACTS[operationId as StudioOperationExactImpactId]
}

export function buildStudioOperationImpactPaths(
  operationId: string,
  params: Record<string, unknown>,
): string[] {
  const contract = getStudioOperationImpactContract(operationId)
  if (!contract) return []
  return [...new Set(contract.paths(params).filter((path) => path.startsWith('/')))]
}
